import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useContentMetafieldData } from '@jbhi-fi/components/src/hooks/useContentMetafieldData/useContentMetafieldData';
import { UseContentProps } from '@jbhi-fi/components/src/types';
import { contentfulApiGenericPageToGenericPageProps } from '../../transformers/contentfulApiGenericPageToGenericPageProps';
import { GenericPageProps } from '../../views/GenericPage/GenericPage';
import { useContentfulEntries } from '@jbhi-fi/components/src/hooks/useContentfulEntries/useContentfulEntries';
import { StackSchema, StackSchemaProps } from '../../components/StackSchema/StackSchema';
import { contentfulApiGenericPageToStackSchema } from '../../transformers/stackSchema/contentfulApiGenericPageToStackSchemaProps';
import { ContentfulGenericPage, EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { serviceHelper } from '../../../../../config/service-helper';
import { SessionStorageKeys, getSession, removeSession } from '@jbhi-fi/utils/src/sessionStorage';

export const GenericPagePropsContext = createContext<UseContentProps<GenericPageProps> | null>(null);

export const GenericPagePropsProvider = ({ children }: { children: ReactNode }) => {
  const metafieldData = useContentMetafieldData<ContentfulGenericPage>();
  const { entries, isLoading, error } = useContentfulEntries<ContentfulGenericPage>({
    filter: {
      content_type: EntryType.genericPage,
      'sys.id': metafieldData.sys.id,
    },
    autoRefresh: window.Jbhifi.isContentPreview,
  });

  const data = entries?.length ? entries[0] : null;
  const finalData = data || metafieldData;

  const genericPage = contentfulApiGenericPageToGenericPageProps(finalData);

  useEffect(() => {
    const addCompetitionToShopifyMetafields = async () => {
      if (finalData?.fields?.parentPage?.fields?.urlSlug === 'competitions') {
        const competitionId = getSession(SessionStorageKeys.CompetitionSubmitted);
        if (competitionId) {
          await fetch(`${serviceHelper.themeApisUrl}/competitions/${competitionId}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          removeSession(SessionStorageKeys.CompetitionSubmitted);
        }
      }
    };

    addCompetitionToShopifyMetafields();
  }, [finalData]);

  return (
    <GenericPagePropsContext.Provider value={{ data: genericPage, isLoading, error }}>
      {finalData?.fields ? (
        <StackSchema {...(contentfulApiGenericPageToStackSchema(finalData) as StackSchemaProps)} />
      ) : null}
      {children}
    </GenericPagePropsContext.Provider>
  );
};

export const useGenericPageProps = (): UseContentProps<GenericPageProps> => {
  const context = useContext(GenericPagePropsContext);
  if (context === undefined || context === null) {
    throw new Error('useGenericPageProps must be used within a GenericPagePropsContext');
  }

  return context;
};
